

































































































































































































































































































































































import {
	computed,
	defineComponent,
	PropType,
	ref,
	watch,
} from '@vue/composition-api';
import ColumnContainer from '@front-app/components/layouts/columns/ColumnContainer.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import PaddedContent from '@front-app/components/layouts/columns/PaddedContent.vue';
import RadioButton from '@front-app/components/inputs/RadioButton.vue';
import RichTextSection from '@front-app/components/form/RichTextSection.vue';
import ScrollingColumn from '@front-app/components/layouts/columns/ScrollingColumn.vue';
import SelectBox from '@front-app/components/inputs/SelectBox.vue';
import SimpleCard from '@front-app/components/cards/SimpleCard.vue';
import StandardErrorText from '@front-app/components/cards/state/StandardErrorText.vue';
import TrCheckbox from '@front-app/components/inputs/Checkbox.vue';
import TrSnackbar from '@front-app/components/Snackbar.vue';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { DropdownPosition } from '@front-app/components/dropdowns/dropdownPosition';
import {
	EventNotification,
	EventNotificationType,
	ReminderTimeBeforeEvent,
	UpsertEventNotification,
} from '@front-app/services/EventsApi/EventModel';
import { getAvailableGroups } from '@front-app/modules/event/util/eventGroups';
import { isEmptyAddress } from '@front-app/services/parse';
import { updateEventNotification } from '@front-app/services/EventsApi/EventsApi';
import { Routes } from '@front-app/constants/routes';
import { Size } from '@front-app/components/constants';
import { syncedRef } from '@front-app/util/reactivity';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { useEventAdminStore } from '@front-app/modules/event/store/EventAdmin';
import { VisibilityOption } from '@front-app/services/GroupsApi/GroupEvent';

export default defineComponent({
	components: {
		ColumnContainer,
		CtaButton,
		ErrorCard,
		LoaderState,
		PaddedContent,
		RadioButton,
		RichTextSection,
		ScrollingColumn,
		SelectBox,
		SimpleCard,
		StandardErrorText,
		TrCheckbox,
		TrSnackbar,
	},
	props: {
		eventId: {
			type: String,
			required: true,
		},
		eventReminder: {
			type: Object as PropType<EventNotification>,
			required: true,
		},
	},
	setup(props, ctx) {
		const eventAdminStore = useEventAdminStore();
		const adminEvent = computed(() => eventAdminStore.getById(props.eventId));

		// Publishing
		const isPublishWarningShown = ref(false);
		const publishStatus = useAsyncStatus();
		const isPublished = computed(() => Boolean(adminEvent.value?.isPublished));
		const togglePublish = () => {
			if (!adminEvent.value) {
				return;
			}

			// Disable publishing an event if neither in-person nor online locations are added
			if (!isPublished.value && (isEmptyAddress(adminEvent.value?.location) && !adminEvent.value?.onlineMeeting.isEnabled)) {
				isPublishWarningShown.value = true;
				return;
			}
			if (isPublishWarningShown.value) {
				isPublishWarningShown.value = false;
			}

			publishStatus.track(eventAdminStore.update(props.eventId, { isPublished: !isPublished.value }));
		};
		// END publishing

		// Registration
		const registrationStatus = useAsyncStatus();
		const isRegistrationEnabled = computed(() => Boolean(adminEvent.value?.allowRegistration));
		const toggleRegistration = () => {
			if (!adminEvent.value) {
				return;
			}
			registrationStatus.track(eventAdminStore.update(props.eventId, { allowRegistration: !isRegistrationEnabled.value }));
		};
		// END Registration

		// Terms of use
		const termsStatus = useAsyncStatus();
		const termsEnabled = syncedRef(() => Boolean(adminEvent.value?.termsEnabled)).ref;
		const requireTermsAcceptance = syncedRef(() => Boolean(adminEvent.value?.requireTermsAcceptance)).ref;
		const termsText = ref(adminEvent.value?.termsAndConditions);
		const haveTermsChanged = ref(false);
		watch(() => [props.eventId, termsText.value, termsEnabled.value], (curr, prev) => {
			if (curr[0] !== prev[0]) {
				haveTermsChanged.value = false;
			} else if (curr[1] === prev[1]) {
				haveTermsChanged.value = true;
			} else if (curr[2] === prev[2]) {
				haveTermsChanged.value = true;
			}
			termsStatus.clear(0);
		});
		const updateTerms = async () => {
			try {
				await termsStatus.track(eventAdminStore.update(props.eventId, {
					termsEnabled: termsEnabled.value,
					requireTermsAcceptance: requireTermsAcceptance.value,
					termsAndConditions: termsText.value,
				}));
				haveTermsChanged.value = false;
			} catch (e) {
				// empty
			}
		};
		// END Terms of use

		// Visibility
		const selectedVisibility = syncedRef(() => adminEvent.value?.visibility).ref;
		const isSelectedVisibility = (target: VisibilityOption): boolean => target === selectedVisibility.value;

		const visibilityStatus = useAsyncStatus();
		const setVisibility = (target: VisibilityOption) => {
			selectedVisibility.value = target;
			visibilityStatus.track(eventAdminStore.update(props.eventId, {
				visibility: selectedVisibility.value,
			}));
		};

		const isCopyLinkMenuOpen = ref(false);
		const showCopyLinkConfirmation = ref(false);

		const copyEventUrl = (groupId: string) => {
			if (!adminEvent.value) {
				return;
			}
			const route = ctx.root.$router.resolve({
				name: Routes.GROUP_EVENT,
				params: {
					eventId: props.eventId,
					groupId,
				},
			});
			const baseUrl = process.env.VUE_APP_TRAPP_PUBLIC_URL || 'https://www.timedright.com';
			const eventUrl = new URL(route.href, baseUrl).href;
			navigator.clipboard.writeText(eventUrl);
			showCopyLinkConfirmation.value = true;
			setTimeout(() => {
				showCopyLinkConfirmation.value = false;
			}, 5000);
		};

		const groupChoices = computed(() => getAvailableGroups(adminEvent.value));
		const selectedGroupId = ref('');
		const selectGroup = (groupId: string) => {
			selectedGroupId.value = groupId;
			copyEventUrl(groupId);
		};
		// END Visibility

		// Updating reminder notification
		const {
			track: trackUpdateEventReminder,
			isError: isErrorUpdatingReminderTimes,
			isLoading: isUpdatingReminderTimes,
			isSuccess: isSuccessUpdatingEventReminder,
		} = useAsyncStatus();
		const reminderTimeBeforeEvent = syncedRef(() => props.eventReminder.metadata?.reminderTimeBeforeEvent || []).ref;
		const setReminderTimesBefore = async (timeBefore: ReminderTimeBeforeEvent) => {
			if (reminderTimeBeforeEvent.value?.includes(timeBefore)) {
				reminderTimeBeforeEvent.value = reminderTimeBeforeEvent.value.filter((option: ReminderTimeBeforeEvent) => option !== timeBefore);
			} else {
				reminderTimeBeforeEvent.value?.push(timeBefore);
			}

			const payload: UpsertEventNotification = {
				subject: props.eventReminder.subject,
				topHtml: props.eventReminder.topHtml,
				bottomHtml: props.eventReminder.bottomHtml,
				reminderTimeBeforeEvent: reminderTimeBeforeEvent.value,
			};

			const updatedReminder = await trackUpdateEventReminder(updateEventNotification(
				props.eventId,
				EventNotificationType.REMINDER,
				payload,
			));

			if (isSuccessUpdatingEventReminder.value) {
				ctx.emit('update-reminder', updatedReminder);
			}
		};
		const clearReminders = async () => {
			reminderTimeBeforeEvent.value = [];
			const payload: UpsertEventNotification = {
				subject: props.eventReminder.subject,
				topHtml: props.eventReminder.topHtml,
				bottomHtml: props.eventReminder.bottomHtml,
				reminderTimeBeforeEvent: [],
			};

			const updatedReminderNotification = await trackUpdateEventReminder(updateEventNotification(
				props.eventId,
				EventNotificationType.REMINDER,
				payload,
			));

			if (isSuccessUpdatingEventReminder.value) {
				ctx.emit('update-reminder', updatedReminderNotification);
			}
		};
		// END Updating reminder notification

		return {
			adminEvent,
			// Publish
			isPublishWarningShown,
			isPublished,
			publishStatus,
			togglePublish,
			// Registration
			isRegistrationEnabled,
			toggleRegistration,
			registrationStatus,
			// Terms of use
			termsStatus,
			termsEnabled,
			termsText,
			requireTermsAcceptance,
			haveTermsChanged,
			updateTerms,
			// Visibility
			VisibilityOption,
			isSelectedVisibility,
			setVisibility,
			visibilityStatus,
			groupChoices,
			selectedGroupId,
			selectGroup,
			isCopyLinkMenuOpen,
			copyEventUrl,
			showCopyLinkConfirmation,
			// Reminders
			reminderTimeBeforeEvent,
			setReminderTimesBefore,
			clearReminders,
			isErrorUpdatingReminderTimes,
			isSuccessUpdatingEventReminder,
			isUpdatingReminderTimes,
			EventNotificationType,
			ReminderTimeBeforeEvent,
			// Util
			ButtonPalettes,
			DropdownPosition,
			Size,
		};
	},
});
